// src/pages/LifecycleEventPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader, AlertTriangle, ChevronLeft, MapPin, Package, User, Building2 } from 'lucide-react';
import Navbar from '../navbar';
import LifecycleEventForm from '../../Dashboard/Lifecycleeventform';

const PRIMARY = '#326BDF';
const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

function getUserRole() {
  try {
    const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.type || payload.role || null;
  } catch { return null; }
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div style={{
      flex: '1 1 180px', minWidth: 160,
      background: '#F8FAFF',
      border: '1px solid #E2EAF8',
      borderRadius: 10, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon size={14} color={PRIMARY} />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      </div>
      <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', margin: 0, wordBreak: 'break-all' }}>
        {value || '—'}
      </p>
    </div>
  );
}

function LifecycleEventPage() {
  const { dppId }  = useParams();
  const navigate   = useNavigate();
  const userRole   = getUserRole();

  const [product, setProduct]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // ── Guard: only Repair/Refurb and Recycler ──
  useEffect(() => {
    if (userRole && !['Repair/Refurb', 'Recycler'].includes(userRole)) {
      navigate('/transaction-data');
    }
  }, [userRole]);

  // ── Fetch product summary ──
  useEffect(() => {
    if (!dppId) return;
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/getproduct/${dppId}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (!res.ok) throw new Error(`Product not found (${res.status})`);
        const data = await res.json();
        setProduct(data);
      } catch (err) {
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [dppId]);

  // ── After form submit → go to product passport ──
  const handleSuccess = () => {
    navigate(`/product-passport/${dppId}`);
  };

  // ── Loading ──
  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading product...</p>
        </div>
      </div>
    </div>
  );

  // ── Error ──
  if (error) return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center max-w-sm mx-auto p-6">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-gray-700 font-medium mb-4">{error}</p>
          <button
            onClick={() => navigate('/transaction-data')}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Header */}
      <div style={{
        background: '#fff', margin: '12px 16px 0',
        borderRadius: 12, padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        <button
          onClick={() => navigate('/transaction-data')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, color: PRIMARY, fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
        >
          <ChevronLeft size={18} />Back
        </button>
        <span style={{ color: PRIMARY, fontWeight: 600, fontSize: 15 }}>
          {userRole === 'Repair/Refurb' ? 'Log Repair Event' : 'Log Recycling Event'}
        </span>
        <div style={{ width: 60 }} />
      </div>

      <div style={{ padding: '20px 16px', maxWidth: 720, margin: '0 auto' }}>

        {/* Product Summary Card */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: 20,
          boxShadow: '0 1px 4px rgba(50,107,223,0.07)',
          marginBottom: 16,
          border: `1px solid #E2EAF8`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: PRIMARY }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Product Summary
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <InfoCard icon={Package}   label="DPP ID"           value={product?.id} />
            <InfoCard icon={Package}   label="Product ID"       value={product?.product_id} />
            <InfoCard icon={Building2} label="Current Location" value={product?.location_id} />
            <InfoCard icon={User}      label="Ownership"        value={product?.ownership} />
          </div>
        </div>

        {/* Lifecycle Event Form */}
        <LifecycleEventForm
          dppId={dppId}
          userRole={userRole}
          locationId={product?.location_id}
          onSubmitSuccess={handleSuccess}
        />

      </div>
    </div>
  );
}

export default LifecycleEventPage;